/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  AuthStore,
  buildPasswordMinimumRule,
  IFormValues,
  useAuthSelector,
} from "@nf/common";
import { useTranslation } from "next-i18next";
import { useState } from "react";
import { useDispatch } from "react-redux";

import CommonTextError from "components/CommonTextError";
import { Form, FormButton, LabelField } from "components/Form";
import { InputPassword } from "components/Inputs";
import { useCountry, useLanguage } from "hooks";

import styles from "./PasswordForm.module.scss";

const PASSWORD_FIELD_NAME = "password";
const PASSWORD_NEW_FIELD_NAME = "password_new";
const FORM_FIELDS = [PASSWORD_FIELD_NAME, PASSWORD_NEW_FIELD_NAME];

export const PasswordForm = () => {
  const { t } = useTranslation("translations");
  const dispatch = useDispatch();

  const { action, isLoading, isProcessing, changePasswordSuccess } =
    useAuthSelector();
  const country = useCountry();
  const language = useLanguage();

  const [isFormSent, setIsFormSent] = useState(false);

  const onFinish = (values: IFormValues) => {
    setIsFormSent(true);

    dispatch(
      AuthStore.Actions.changePassword({
        password: values[PASSWORD_FIELD_NAME],
        password_new: values[PASSWORD_NEW_FIELD_NAME],
        country,
        language,
      })
    );
  };

  const onValuesChange = () => {
    setIsFormSent(false);

    if (changePasswordSuccess) {
      dispatch(AuthStore.Actions.clearChangePasswordSuccess());
    }
  };

  return (
    <div className={styles.PasswordForm}>
      <Form onFinish={onFinish} onValuesChange={onValuesChange}>
        <LabelField
          name="password"
          label={t("web_new_password_current_password_header")}
          rules={[buildPasswordMinimumRule()]}
          validateTrigger={["onBlur", "onSubmit"]}
        >
          <InputPassword
            placeholder={t("web_new_password_current_password_field")}
          />
        </LabelField>

        <LabelField
          name="password_new"
          label={t("web_new_password_header")}
          tip={t("web_new_password_text")}
          rules={[buildPasswordMinimumRule()]}
          validateTrigger={["onBlur", "onSubmit"]}
        >
          <InputPassword placeholder={t("web_new_password_field")} />
        </LabelField>

        {changePasswordSuccess && (
          <p className={styles.success}>{t("web_login_password_updated")}</p>
        )}

        <div className={styles.ButtonLine}>
          <FormButton
            disabled={isLoading || isFormSent}
            loading={isProcessing}
            nonEmptyFields={{
              requiredFields: FORM_FIELDS,
              autoCompleted: false,
            }}
          >
            {t("web_new_password_submit")}
          </FormButton>
        </div>
      </Form>
      <p className={styles.information}>{t("web_new_password_information")}</p>
      {action?.type === AuthStore.Consts.CHANGE_PASSWORD_FAILURE && (
        <CommonTextError
          className={styles.error}
          translationKey="web_my_account_menu_change_password_error"
        />
      )}
    </div>
  );
};
